import React from 'react';
import { ReactComponent as Info } from 'assets/icons/info-gray.svg';
import { ReactComponent as Eye } from 'assets/icons/pwEye.svg';
import { ReactComponent as Error } from 'assets/icons/big-warning.svg';
import { ReactComponent as Success } from 'assets/icons/checkActive.svg';
import { twJoin, twMerge } from 'tailwind-merge';
import { Persona_Inquiry_Status_Enum } from 'graphql/generated/hasura';
import CustomTooltip from 'components/CustomTooltip';
import ButtonComponent from 'components/button/buttonComponent';
import { useGetPage } from 'hooks/useGetPage';
import { pageIds } from 'utilities/constants';
import { Link } from 'react-router-dom';
import { ERROR_STATUSES, PENDING_STATUSES } from 'const/persona-statuses';

interface ActionFieldProps {
  title: string;
  info: string;
  buttonLabel: string;
  error?: string;
  link?: string;
  success: React.ReactNode;
  actions?: React.ReactNode;
  successLabel: string;
  handleSubmit: () => void;
  status?: Persona_Inquiry_Status_Enum | null;
  disabled: boolean;
  tooltip?: string;
  buttonClassName?: string;
  titleClassName?: string;
  pendingStatuses?: Persona_Inquiry_Status_Enum[];
}

const ActionField: React.FC<ActionFieldProps> = ({
  title,
  info,
  buttonLabel,
  status,
  handleSubmit,
  tooltip,
  disabled,
  success,
  successLabel,
  pendingStatuses = PENDING_STATUSES,
  error,
  link,
  actions,
  buttonClassName,
  titleClassName,
}) => {
  const { data: locale } = useGetPage({
    locale: 'en',
    pageId: pageIds.PROVIDER_USER_PENDING_REVIEW,
  });

  const isComplete = status === Persona_Inquiry_Status_Enum.Approved;
  const isPending = pendingStatuses.includes(
    status as Persona_Inquiry_Status_Enum,
  );
  const isError = ERROR_STATUSES.includes(
    status as Persona_Inquiry_Status_Enum,
  );

  return (
    <div
      onClick={() =>
        isComplete || isPending || disabled ? () => ({}) : handleSubmit()
      }
      className={twMerge(
        'py-4 cursor-pointer hover:bg-hover-blue px-5 flex desktop:items-center desktop:flex-row flex-col bg-base-content border-gray-opacity-15 gap-2.5 desktop:gap-30 rounded-10',
        (isComplete || isPending || disabled) &&
          'cursor-not-allowed hover:bg-base-content',
      )}
    >
      <h4
        className={twMerge(
          'text-h4 whitespace-nowrap desktop:order-1 order-2 text-neutral-800 font-semibold',
          titleClassName,
        )}
      >
        {title}
      </h4>
      {!isError && !isComplete && (
        <p className="flex desktop:order-2 order-3 gap-2.5 items-center text-med-gray-3 text-sm font-medium mr-auto">
          <Info className="w-5 h-5 min-w-[1.25rem]" />
          {info}
        </p>
      )}
      {isError && (
        <p className="flex desktop:order-2 order-3 gap-2.5 items-center text-clc-red text-sm font-medium mr-auto">
          <Error className="w-5 h-5 min-w-[1.25rem]" />
          {error}
        </p>
      )}
      {isComplete && (
        <p className="flex desktop:order-2 order-3 gap-2.5 items-center text-bright-green text-sm font-medium mr-auto">
          <Success className="w-5 stroke-bright-green h-5 min-w-[1.25rem]" />
          {success}
        </p>
      )}
      <p
        className={twJoin(
          'flex whitespace-nowrap desktop:order-3 order-1 gap-1.5 text-base desktop:text-base font-medium text-clc-red',
          isComplete && '!text-bright-green',
          isPending && '!text-amber-border',
        )}
      >
        <span
          className={twJoin(
            'w-5 h-5 min-w-[1.25rem] rounded-full bg-clc-red',
            isComplete && '!bg-bright-green',
            isPending && '!bg-amber-border',
          )}
        />
        {successLabel}
        {tooltip && isPending && (
          <Info
            data-tooltip-id={`${title}-tooltip`}
            className="w-5 h-5 min-w-[1.25rem]"
          />
        )}
        <CustomTooltip
          id={`${title}-tooltip`}
          content={tooltip}
          className="z-40 max-w-[410px] whitespace-normal py-2 px-4"
          place="top"
        />
      </p>
      <div className="order-4 flex desktop:flex-row flex-col gap-2.5">
        <ButtonComponent
          className={twMerge(
            'order-5 desktop:ml-2.5 whitespace-nowrap',
            buttonClassName,
          )}
          testID={`${title}-btn`}
          Icon={Success}
          iconPosition="left"
          iconWidth={twJoin(
            'w-5 !stroke-white !text-sm h-5 min-w-[1.25rem]',
            !isComplete && 'hidden',
          )}
          showSpinner={isPending}
          disabled={isComplete || isPending || disabled}
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            handleSubmit();
          }}
        >
          {isComplete ? locale?.completed : buttonLabel}
        </ButtonComponent>
        {link && (
          <Link
            onClick={(event) => event.stopPropagation()}
            to={link}
            target="_blank"
            className="group text-sm flex max-desktop:w-full underline items-center gap-1.5 text-clc-blue font-semibold uppercase"
          >
            <Eye className="w-5 h-5 min-w-[1.25rem] fill-clc-blue" />
            <span>View</span>
          </Link>
        )}
        {!isComplete && actions}
      </div>
    </div>
  );
};

export default ActionField;
